// ============================================================================
// A trip's route: a stop timeline beside a map, the two kept in step.
//
// THE LABEL IS LOGINEXT'S OWN SEQUENCE, with a letter for the kind of stop —
// one numbering across the whole trip, shared by collections and drops, so a
// mixed trip reads exactly as LogiNext planned it:  P1 … P5 → D6 → P7 → D8
//
// WHEN THE TRIP UPDATE NAMES A SHIPMENT the backend sends BOTH of its stops, so
// a last-mile trip reads P1…P12 → D13…D24 instead of starting at D13. Those
// stops carry no "other end": it is drawn as a stop of its own. A shipment only
// the planning feed describes still gets its far end as a plain W / P marker.
//
// THE SIDEBAR IS THE READING SURFACE, THE MAP IS THE WHERE. Consecutive stops at
// one place (twelve collections at our own dock) are one entry in the timeline
// and one counted pin on the map; nothing is hidden, only folded until opened.
//
// TIMES: LogiNext sends "YYYY-MM-DD HH:mm:ss" in UTC with no zone marker — a
// stop planned for "11:48" was delivered at 11:26 UTC, 16:56 in Bengaluru.
// Everything here is shown in IST.
//
// ONE FILE, TWO APPS: this is copied verbatim into the Carrier Portal
// (md-carrier-portal/src/pages/dashboard/trip_map.tsx); only the two imports
// differ. A change made here has to be made there.
// ============================================================================
import {
  DirectionsRenderer,
  DirectionsService,
  GoogleMap,
  InfoWindowF,
  MarkerF,
  PolylineF,
  useLoadScript,
} from "@react-google-maps/api";
import { Button, Segmented, Tag, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GOOGLE_MAPS_API_KEY } from "../../../components/misc/map-picker/center-map";
import { TripStop } from "../logistics/logistics.api";

// POIs and administrative labels off — on an ops map they compete with the stops.
const MAP_STYLE = [
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

const PICKUP = "#2a78d6"; // the validated categorical blue — same pair the Summary uses
const DELIVER = "#eb6834"; // …and its orange
const DEPOT = "#4a3aa7"; // the warehouse — distinct from both stop kinds
const ROUTE = "#24aab7"; // the app's own primary: a third hue that competes with neither marker
const DONE = "#16a34a";
const FAILED = "#dc2626";
const INK = "#111827";
const MUTED = "#6b7280";
const LINE = "#e5e7eb";

// Google's Directions cap applies to the waypoints BETWEEN origin and destination.
const MAX_WAYPOINTS = 23;

// Within this distance two consecutive stops are the same place to drive to. Set above the ~30m
// LogiNext's coordinates drift between rows for one dock, and well below any real next stop.
const SAME_PLACE_M = 75;

// Past this zoom, a group that still overlaps is listed rather than zoomed again — Google caps out
// around 21, and a click that visibly does nothing reads as broken.
const MAX_SPLIT_ZOOM = 18;

// Below this width the sidebar stacks above the map — the Carrier Portal is opened on phones.
const STACK_BELOW_PX = 760;

// Timeline entries with more stops than this start folded.
const FOLD_OVER = 3;

// Equirectangular distance — exact enough at city scale, and all this ever compares is "is it tiny".
const metresBetween = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const rad = Math.PI / 180;
  const x = (b.lng - a.lng) * rad * Math.cos(((a.lat + b.lat) / 2) * rad);
  const y = (b.lat - a.lat) * rad;
  return Math.hypot(x, y) * 6371000;
};

// A pin has to fit its own label: "D4" is two characters, "D18" three. Both pin and type scale.
const pinSize = (label: string) => {
  const n = label.length;
  if (n <= 2) return { radius: 12, half: 9, fontSize: "10px" };
  if (n === 3) return { radius: 14, half: 11, fontSize: "9px" };
  return { radius: 16, half: 13, fontSize: "8px" };
};

// How far a leg bows, as a fraction of its own length, and how smooth the arc is.
const CURVE_BOW = 0.12;
const CURVE_STEPS = 24;

// --- Time -----------------------------------------------------------------------------------------
const IST_TIME = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});
const IST_DAY = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  day: "numeric",
  month: "short",
});

// LogiNext's bare "YYYY-MM-DD HH:mm:ss" is UTC; an ISO string with an offset parses as itself.
const parseWhen = (raw?: string | null): Date | null => {
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(raw.trim());
  const d = m
    ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)))
    : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "4:34 pm" today, "27 Sep · 4:34 pm" on any other day — the day only when it is news.
const fmtWhen = (raw?: string | null): string | null => {
  const d = parseWhen(raw);
  if (!d) return null;
  const time = IST_TIME.format(d).replace(/\s?([AP])M$/i, (_, p) => ` ${p.toLowerCase()}m`);
  const day = IST_DAY.format(d);
  return day === IST_DAY.format(new Date()) ? time : `${day} · ${time}`;
};

// --- Grouping overlapping pins ------------------------------------------------------------------
// By hand rather than with @googlemaps/markerclusterer: that wrapper hands every marker to the
// clusterer, and when its render step produces nothing the pins vanish with it. Two pins "overlap"
// when they are within CLUSTER_PX screen pixels AT THE CURRENT ZOOM.
const CLUSTER_PX = 42;

const worldPixel = (lat: number, lng: number, zoom: number) => {
  const scale = 256 * Math.pow(2, zoom);
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)) * scale,
  };
};

function groupByProximity<T extends { lat: number; lng: number }>(pins: T[], zoom: number): T[][] {
  const groups: Array<{ px: { x: number; y: number }; members: T[] }> = [];
  for (const pin of pins) {
    const px = worldPixel(pin.lat, pin.lng, zoom);
    const hit = groups.find((g) => Math.hypot(g.px.x - px.x, g.px.y - px.y) < CLUSTER_PX);
    if (hit) hit.members.push(pin);
    else groups.push({ px, members: [pin] });
  }
  return groups.map((g) => g.members);
}

// --- Container width, for the stacked layout --------------------------------------------------------
function useWidth(ref: React.RefObject<HTMLElement>) {
  const [width, setWidth] = useState(1200);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

// --- Types ------------------------------------------------------------------------------------------
type Kind = "pickup" | "drop";
type Status = "done" | "failed" | "pending";
type Filter = "all" | "pickup" | "drop";

interface RunPoint {
  i: number; // index into the run — the one id every part of this file shares
  kind: Kind;
  label: string;
  unsequenced: boolean;
  status: Status;
  lat: number;
  lng: number;
  stop: TripStop;
}

interface TripMeta {
  trip_name: string;
  driver: string | null;
  phone: string | null;
  planned_at: string | null;
}

interface Props {
  stops: TripStop[];
  liveLocation?: { lat: number; lng: number } | null;
  height?: number | string;
  trip?: TripMeta | null;
}

const kindColor = (k: Kind) => (k === "pickup" ? PICKUP : DELIVER);

const placeName = (p: RunPoint) => {
  if (p.kind === "pickup") return p.stop.at_warehouse ? "Our warehouse" : "Pickup point";
  return p.stop.client_name || (p.stop.at_warehouse ? "Our warehouse" : "Drop point");
};

// The one line under a stop that says where it stands: what happened, or what is expected.
const whenLine = (p: RunPoint): { text: string; color: string } | null => {
  const s = p.stop;
  if (p.status === "done") {
    const at = fmtWhen(s.done_at);
    return { text: `${p.kind === "pickup" ? "Collected" : "Delivered"}${at ? ` ${at}` : ""}`, color: DONE };
  }
  if (p.status === "failed") {
    const at = fmtWhen(s.done_at);
    return { text: `Attempt failed${at ? ` ${at}` : ""}`, color: FAILED };
  }
  const eta = fmtWhen(p.kind === "pickup" ? s.eta_start || s.eta_end : s.eta_end || s.eta_start);
  if (eta) return { text: `ETA ${eta}`, color: MUTED };
  const by = fmtWhen(s.window_end);
  return by ? { text: `Window ends ${by}`, color: MUTED } : null;
};

// --- The badge — the same shape and colour in the list as on the map ----------------------------
const Badge: React.FC<{ p: RunPoint; big?: boolean }> = ({ p, big }) => {
  const size = big ? 30 : 26;
  const done = p.status === "done";
  return (
    <span
      style={{
        flex: "0 0 auto",
        width: size,
        height: size,
        borderRadius: p.kind === "pickup" ? 6 : "50%",
        background: kindColor(p.kind),
        opacity: done || p.unsequenced ? 0.45 : 1,
        color: "#fff",
        fontSize: p.label.length > 3 ? 9 : 10.5,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow:
          p.status === "failed" ? `0 0 0 2px ${FAILED}` : "0 1px 2px rgba(0,0,0,.18)",
      }}
    >
      {p.label}
    </span>
  );
};

// ================================================================================================
const TripMap: React.FC<Props> = ({ stops, liveLocation, height, trip }) => {
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });
  const rootRef = useRef<HTMLDivElement>(null);
  const width = useWidth(rootRef);
  const stacked = width < STACK_BELOW_PX;

  const mapRef = useRef<google.maps.Map | null>(null);
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [zoom, setZoom] = useState(12);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [routeFailed, setRouteFailed] = useState(false);

  const [selected, setSelected] = useState<number | null>(null); // run index; pans + halo + info
  const [hovered, setHovered] = useState<number | null>(null); // run index; halo only
  const [openGroup, setOpenGroup] = useState<string | null>(null); // a map cluster's stop list
  const [filter, setFilter] = useState<Filter>("all");
  const [unfolded, setUnfolded] = useState<Set<string>>(new Set());

  // THE RUN: one point per row, in LogiNext's order (the backend sorts it).
  const run: RunPoint[] = useMemo(
    () =>
      stops.map((stop, i) => {
        const kind: Kind = stop.type === "pickup" ? "pickup" : "drop";
        return {
          i,
          kind,
          // A shipment LogiNext never sequenced gets the bare letter — no invented number.
          label: `${kind === "pickup" ? "P" : "D"}${stop.seq ?? ""}`,
          unsequenced: !!stop.unsequenced,
          status: (stop.status as Status) || "pending",
          lat: stop.lat,
          lng: stop.lng,
          stop,
        };
      }),
    [stops]
  );

  const visible = useCallback(
    (p: RunPoint) => filter === "all" || p.kind === filter,
    [filter]
  );

  // The first stop still to come, in run order — what the driver is heading for.
  const nextIdx = useMemo(
    () => run.find((p) => !p.unsequenced && p.status === "pending")?.i ?? null,
    [run]
  );

  const counts = useMemo(() => {
    const c = { pickup: 0, drop: 0, pickupDone: 0, dropDone: 0, failed: 0 };
    for (const p of run) {
      if (p.kind === "pickup") {
        c.pickup += 1;
        if (p.status === "done") c.pickupDone += 1;
      } else {
        c.drop += 1;
        if (p.status === "done") c.dropDone += 1;
      }
      if (p.status === "failed") c.failed += 1;
    }
    return c;
  }, [run]);
  const doneAll = counts.pickupDone + counts.dropDone;

  // The far ends a planning row names but no stop covers — our docks ("W") and other sites ("P").
  const others = useMemo(() => {
    const seen = new Map<string, { lat: number; lng: number; isWarehouse: boolean; count: number }>();
    for (const s of stops) {
      if (s.other_lat == null || s.other_lng == null) continue;
      const key = `${s.other_lat.toFixed(4)},${s.other_lng.toFixed(4)}`;
      const hit = seen.get(key);
      if (hit) hit.count += 1;
      else
        seen.set(key, { lat: s.other_lat, lng: s.other_lng, isWarehouse: !!s.other_is_warehouse, count: 1 });
    }
    return Array.from(seen.values());
  }, [stops]);

  // THE LINE: the sequenced stops in order, consecutive stops at one place counted once — routing
  // between points 30m apart inside a warehouse draws nonsense and spends a Directions waypoint.
  const dropPath = useMemo(() => {
    const out: Array<{ lat: number; lng: number }> = [];
    for (const p of run) {
      if (p.unsequenced) continue;
      const prev = out[out.length - 1];
      if (prev && metresBetween(prev, p) < SAME_PLACE_M) continue;
      out.push({ lat: p.lat, lng: p.lng });
    }
    return out;
  }, [run]);

  const canRoute = dropPath.length >= 2 && dropPath.length - 2 <= MAX_WAYPOINTS;

  const directionsCallback = useCallback(
    (result: google.maps.DirectionsResult | null, status: google.maps.DirectionsStatus) => {
      if (status === "OK" && result) setDirections(result);
      else setRouteFailed(true); // fall back to the curve rather than drawing nothing
    },
    []
  );

  // A gentle arc per leg — says "between these two" without claiming to be a road.
  const curvedPath = useMemo(() => {
    const out: Array<{ lat: number; lng: number }> = [];
    for (let k = 0; k < dropPath.length - 1; k += 1) {
      const a = dropPath[k];
      const b = dropPath[k + 1];
      const cx = (a.lat + b.lat) / 2 + (b.lng - a.lng) * CURVE_BOW;
      const cy = (a.lng + b.lng) / 2 - (b.lat - a.lat) * CURVE_BOW;
      for (let s = 0; s <= CURVE_STEPS; s += 1) {
        const u = s / CURVE_STEPS;
        const n = (1 - u) * (1 - u);
        const m = 2 * (1 - u) * u;
        const o = u * u;
        out.push({ lat: n * a.lat + m * cx + o * b.lat, lng: n * a.lng + m * cy + o * b.lng });
      }
    }
    return out;
  }, [dropPath]);

  // --- The timeline: consecutive stops of one kind at one place fold into one entry ---------------
  const timeline = useMemo(() => {
    const entries: Array<{ key: string; members: RunPoint[] }> = [];
    for (const p of run) {
      if (p.unsequenced || !visible(p)) continue;
      const last = entries[entries.length - 1];
      const head = last?.members[0];
      if (head && head.kind === p.kind && metresBetween(head, p) < SAME_PLACE_M) last.members.push(p);
      else entries.push({ key: `e-${p.i}`, members: [p] });
    }
    return entries;
  }, [run, visible]);

  const unsequenced = useMemo(() => run.filter((p) => p.unsequenced && visible(p)), [run, visible]);

  // --- Map pins --------------------------------------------------------------------------------------
  const pins = useMemo(
    () => [
      ...(filter === "drop"
        ? []
        : others.map((o, k) => ({ pin: "other" as const, key: `other-${k}`, lat: o.lat, lng: o.lng, o }))),
      ...run
        .filter(visible)
        .map((point) => ({ pin: "stop" as const, key: `stop-${point.i}`, lat: point.lat, lng: point.lng, point })),
    ],
    [others, run, visible, filter]
  );
  const groups = useMemo(() => groupByProximity(pins, zoom), [pins, zoom]);

  // --- Camera ---------------------------------------------------------------------------------------
  const fitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map || !run.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    run.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    others.forEach((o) => bounds.extend({ lat: o.lat, lng: o.lng }));
    if (liveLocation) bounds.extend(liveLocation);
    map.fitBounds(bounds, 56);
    window.google.maps.event.addListenerOnce(map, "idle", () => setZoom(map.getZoom() ?? 12));
  }, [run, others, liveLocation]);

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    fitAll();
  };

  // Selecting from the LIST flies the map there; selecting from the MAP scrolls the list there.
  const select = useCallback(
    (i: number, from: "list" | "map") => {
      setOpenGroup(null);
      setSelected(i);
      const p = run[i];
      if (!p) return;
      const entry = timeline.find((e) => e.members.some((m) => m.i === i));
      if (entry && entry.members.length > FOLD_OVER) {
        setUnfolded((prev) => new Set(prev).add(entry.key));
      }
      if (from === "list") {
        const map = mapRef.current;
        if (map) {
          map.panTo({ lat: p.lat, lng: p.lng });
          if ((map.getZoom() ?? 12) < 15) map.setZoom(15);
        }
      } else {
        // After the unfold renders.
        window.setTimeout(
          () => rowRefs.current[i]?.scrollIntoView({ block: "nearest", behavior: "smooth" }),
          40
        );
      }
    },
    [run, timeline]
  );

  const focus = selected ?? hovered;
  const focusPoint = focus != null ? run[focus] : null;

  if (!isLoaded) return <div style={{ padding: 24, color: MUTED }}>Loading map…</div>;
  if (!stops.length) return <div style={{ padding: 24, color: MUTED }}>No stops to plot.</div>;

  // ================================================================================================
  // SIDEBAR
  // ================================================================================================
  const stat = (label: string, done: number, total: number, color: string) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: INK, lineHeight: 1.1 }}>
        {done}
        <span style={{ fontSize: 12, fontWeight: 500, color: MUTED }}> / {total}</span>
      </div>
      <div style={{ height: 4, background: LINE, borderRadius: 2, marginTop: 6, overflow: "hidden" }}>
        <div
          style={{
            width: total ? `${(done / total) * 100}%` : 0,
            height: "100%",
            background: color,
            borderRadius: 2,
            transition: "width .3s",
          }}
        />
      </div>
    </div>
  );

  const stopRow = (p: RunPoint, indent = false) => {
    const when = whenLine(p);
    const isSel = selected === p.i;
    const isNext = nextIdx === p.i;
    return (
      <div
        key={p.i}
        ref={(el) => {
          rowRefs.current[p.i] = el;
        }}
        onClick={() => select(p.i, "list")}
        onMouseEnter={() => setHovered(p.i)}
        onMouseLeave={() => setHovered(null)}
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          padding: indent ? "7px 10px 7px 18px" : "9px 10px",
          borderRadius: 8,
          cursor: "pointer",
          background: isSel ? "#ecfeff" : hovered === p.i ? "#f9fafb" : "transparent",
          boxShadow: isSel ? `inset 3px 0 0 ${ROUTE}` : "none",
        }}
      >
        <Badge p={p} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: INK,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {indent ? p.stop.provider_ref || "—" : placeName(p)}
            </span>
            {isNext && (
              <Tag color="cyan" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 5px" }}>
                NEXT
              </Tag>
            )}
          </div>
          {!indent && p.stop.provider_ref && (
            <div style={{ fontSize: 11.5, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {p.stop.provider_ref}
            </div>
          )}
          {when && <div style={{ fontSize: 11.5, color: when.color, marginTop: 1 }}>{when.text}</div>}
        </div>
      </div>
    );
  };

  const entryBlock = (entry: { key: string; members: RunPoint[] }) => {
    if (entry.members.length === 1) return stopRow(entry.members[0]);
    const first = entry.members[0];
    const last = entry.members[entry.members.length - 1];
    const open = entry.members.length <= FOLD_OVER || unfolded.has(entry.key);
    const done = entry.members.filter((m) => m.status === "done").length;
    const failed = entry.members.filter((m) => m.status === "failed").length;
    const rangeP: RunPoint = { ...first, label: `${first.label}–${last.label.slice(1)}` };
    return (
      <div key={entry.key} style={{ borderRadius: 8, border: `1px solid ${LINE}`, margin: "4px 0" }}>
        <div
          onClick={() => {
            setUnfolded((prev) => {
              const next = new Set(prev);
              if (next.has(entry.key)) next.delete(entry.key);
              else next.add(entry.key);
              return next;
            });
            const map = mapRef.current;
            if (map) {
              map.panTo({ lat: first.lat, lng: first.lng });
              if ((map.getZoom() ?? 12) < 15) map.setZoom(15);
            }
          }}
          style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 10px", cursor: "pointer" }}
        >
          <Badge p={{ ...rangeP, status: done === entry.members.length ? "done" : "pending" }} big />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>
              {entry.members.length} {first.kind === "pickup" ? "pickups" : "drops"} · {placeName(first)}
            </div>
            <div style={{ fontSize: 11.5, color: failed ? FAILED : done ? DONE : MUTED }}>
              {done} of {entry.members.length} done
              {failed ? ` · ${failed} failed` : ""}
            </div>
          </div>
          {entry.members.length > FOLD_OVER && (
            <span style={{ color: MUTED, fontSize: 12, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>
              ▸
            </span>
          )}
        </div>
        {open && <div style={{ paddingBottom: 4 }}>{entry.members.map((m) => stopRow(m, true))}</div>}
      </div>
    );
  };

  const sidebar = (
    <div
      style={{
        flex: stacked ? "0 0 44%" : "0 0 360px",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: stacked ? "none" : `1px solid ${LINE}`,
        borderBottom: stacked ? `1px solid ${LINE}` : "none",
        background: "#fff",
      }}
    >
      {/* Who and when */}
      <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${LINE}` }}>
        {trip?.driver && (
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: INK }}>{trip.driver}</span>
            {trip.phone && (
              <a href={`tel:${trip.phone}`} style={{ fontSize: 12.5, color: ROUTE }}>
                {trip.phone}
              </a>
            )}
          </div>
        )}
        {trip?.planned_at && (
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>Planned {fmtWhen(trip.planned_at)}</div>
        )}

        <div style={{ display: "flex", gap: 14, marginTop: 12 }}>
          {stat("Pickups", counts.pickupDone, counts.pickup, PICKUP)}
          {stat("Drops", counts.dropDone, counts.drop, DELIVER)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>Progress</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: INK, lineHeight: 1.1 }}>
              {run.length ? Math.round((doneAll / run.length) * 100) : 0}%
            </div>
            <div style={{ fontSize: 11, color: counts.failed ? FAILED : MUTED, marginTop: 6 }}>
              {counts.failed ? `${counts.failed} failed` : `${run.length - doneAll} to go`}
            </div>
          </div>
        </div>

        <Segmented
          block
          size="small"
          style={{ marginTop: 12 }}
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          options={[
            { label: `All ${run.length}`, value: "all" },
            { label: `Pickups ${counts.pickup}`, value: "pickup" },
            { label: `Drops ${counts.drop}`, value: "drop" },
          ]}
        />
      </div>

      {/* The run */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "6px 8px 12px" }}>
        {timeline.map(entryBlock)}
        {!timeline.length && !unsequenced.length && (
          <div style={{ padding: 20, color: MUTED, fontSize: 12.5 }}>No stops of this kind.</div>
        )}
        {unsequenced.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: MUTED, padding: "14px 10px 4px", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Not in LogiNext's plan · {unsequenced.length}
            </div>
            {unsequenced.map((p) => stopRow(p))}
          </>
        )}
      </div>
    </div>
  );

  // ================================================================================================
  // MAP
  // ================================================================================================
  const markerFor = (point: RunPoint, key: string) => {
    const isPickup = point.kind === "pickup";
    const size = pinSize(point.label);
    const done = point.status === "done";
    const isNext = nextIdx === point.i;
    return (
      <MarkerF
        key={key}
        position={{ lat: point.lat, lng: point.lng }}
        zIndex={isNext ? 900 : done ? 100 : 500}
        onClick={() => select(point.i, "map")}
        onMouseOver={() => setHovered(point.i)}
        onMouseOut={() => setHovered(null)}
        label={{ text: point.label, color: "#fff", fontSize: size.fontSize, fontWeight: "700" }}
        icon={{
          // Squares collect, circles deliver — so the two never read alike, independent of colour.
          path: isPickup
            ? `M -${size.half},-${size.half} ${size.half},-${size.half} ${size.half},${size.half} -${size.half},${size.half} z`
            : window.google.maps.SymbolPath.CIRCLE,
          scale: isPickup ? 1 : size.radius,
          fillColor: kindColor(point.kind),
          // Done stops recede; what is left to do stays loud.
          fillOpacity: done || point.unsequenced ? 0.45 : 1,
          strokeColor: point.status === "failed" ? FAILED : isNext ? INK : "#fff",
          strokeWeight: point.status === "failed" || isNext ? 3 : 2,
        }}
      />
    );
  };

  const legendItem = (swatch: React.ReactNode, text: string) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {swatch}
      {text}
    </span>
  );

  const map = (
    <div style={{ flex: 1, minHeight: 0, minWidth: 0, position: "relative" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        onLoad={onLoad}
        onZoomChanged={() => setZoom(mapRef.current?.getZoom() ?? zoom)}
        onClick={() => {
          setSelected(null);
          setOpenGroup(null);
        }}
        options={{
          styles: MAP_STYLE,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: "greedy",
        }}
      >
        {/* The run. Real roads when Directions can carry it, a curve when it cannot. */}
        {canRoute && !routeFailed && !directions && (
          <DirectionsService
            options={{
              origin: dropPath[0],
              destination: dropPath[dropPath.length - 1],
              waypoints: dropPath.slice(1, -1).map((p) => ({ location: p, stopover: true })),
              travelMode: google.maps.TravelMode.DRIVING,
              // NEVER optimise: LogiNext already decided this order and the driver is following it.
              optimizeWaypoints: false,
            }}
            callback={directionsCallback}
          />
        )}
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: { strokeColor: ROUTE, strokeOpacity: 0.85, strokeWeight: 4 },
            }}
          />
        )}
        {(!canRoute || routeFailed) && curvedPath.length > 0 && (
          <PolylineF
            path={curvedPath}
            options={{ strokeColor: ROUTE, strokeOpacity: 0.8, strokeWeight: 3, geodesic: true }}
          />
        )}

        {groups.map((group) => {
          // Several pins on one spot: one counted circle.
          if (group.length > 1) {
            const count = group.length;
            const stopsIn = group.filter((g) => g.pin === "stop") as Array<{ point: RunPoint }>;
            const allDone = stopsIn.length > 0 && stopsIn.every((g) => g.point.status === "done");
            return (
              <MarkerF
                key={`cluster-${group[0].key}`}
                position={{ lat: group[0].lat, lng: group[0].lng }}
                zIndex={1000 + count}
                onClick={() => {
                  const m = mapRef.current;
                  if (!m) return;
                  // Pins on one spot never come apart however far you zoom — list them instead.
                  const onOneSpot = group.every((g) => metresBetween(group[0], g) < SAME_PLACE_M);
                  if (onOneSpot || (m.getZoom() ?? 12) >= MAX_SPLIT_ZOOM) {
                    setSelected(null);
                    setOpenGroup(group[0].key);
                    return;
                  }
                  const bounds = new window.google.maps.LatLngBounds();
                  group.forEach((g) => bounds.extend({ lat: g.lat, lng: g.lng }));
                  m.fitBounds(bounds, 64);
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: count < 10 ? 16 : count < 50 ? 19 : 22,
                  fillColor: "#ffffff",
                  fillOpacity: 1,
                  strokeColor: allDone ? DONE : ROUTE,
                  strokeWeight: 3,
                }}
                label={{ text: String(count), color: INK, fontSize: count > 99 ? "11px" : "12px", fontWeight: "700" }}
                title={`${count} stops here — click to see them`}
              >
                {openGroup === group[0].key && (
                  <InfoWindowF onCloseClick={() => setOpenGroup(null)}>
                    <div style={{ fontSize: 12, lineHeight: 1.45, width: 250, maxHeight: 300, overflowY: "auto" }}>
                      <div style={{ fontWeight: 700, marginBottom: 6, color: INK }}>{count} stops here</div>
                      {group.map((g) => {
                        if (g.pin === "other") {
                          return (
                            <div key={g.key} style={{ padding: "4px 0" }}>
                              <Tag color="purple">{g.o.isWarehouse ? "W" : "P"}</Tag>
                              {g.o.isWarehouse ? "Our warehouse" : "Pickup point"} · {g.o.count} shipment
                              {g.o.count === 1 ? "" : "s"}
                            </div>
                          );
                        }
                        const p = g.point;
                        const when = whenLine(p);
                        return (
                          <div
                            key={g.key}
                            onClick={() => select(p.i, "map")}
                            style={{ display: "flex", gap: 8, padding: "5px 0", borderTop: `1px solid ${LINE}`, cursor: "pointer" }}
                          >
                            <Badge p={p} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: INK }}>{p.stop.provider_ref || "—"}</div>
                              {p.kind === "drop" && p.stop.client_name && (
                                <div style={{ color: MUTED }}>{p.stop.client_name}</div>
                              )}
                              {when && <div style={{ color: when.color }}>{when.text}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </InfoWindowF>
                )}
              </MarkerF>
            );
          }

          const only = group[0];
          if (only.pin === "other") {
            const o = only.o;
            return (
              <MarkerF
                key={only.key}
                position={{ lat: o.lat, lng: o.lng }}
                title={`${o.isWarehouse ? "Our warehouse" : "Pickup point"} · ${o.count} shipment${o.count === 1 ? "" : "s"}`}
                label={{ text: o.isWarehouse ? "W" : "P", color: "#fff", fontSize: "10px", fontWeight: "700" }}
                icon={{
                  path: "M -10,-10 10,-10 10,10 -10,10 z",
                  scale: 1,
                  fillColor: o.isWarehouse ? DEPOT : PICKUP,
                  fillOpacity: 1,
                  strokeColor: "#fff",
                  strokeWeight: 2,
                }}
              />
            );
          }
          return markerFor(only.point, only.key);
        })}

        {/* The halo — drawn on top of everything, so a selected stop is findable even inside a cluster. */}
        {focusPoint && (
          <MarkerF
            position={{ lat: focusPoint.lat, lng: focusPoint.lng }}
            zIndex={2000}
            clickable={false}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 22,
              fillColor: ROUTE,
              fillOpacity: 0.12,
              strokeColor: ROUTE,
              strokeWeight: 3,
            }}
          />
        )}

        {/* The selected stop's card — standalone, so it opens even when its pin is folded into a cluster. */}
        {selected != null && run[selected] && (
          <InfoWindowF
            position={{ lat: run[selected].lat, lng: run[selected].lng }}
            onCloseClick={() => setSelected(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -14) }}
          >
            {(() => {
              const p = run[selected];
              const s = p.stop;
              const when = whenLine(p);
              const windowFrom = fmtWhen(s.window_start);
              const windowTo = fmtWhen(s.window_end);
              return (
                <div style={{ fontSize: 12, lineHeight: 1.5, width: 230 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Badge p={p} />
                    <div>
                      <div style={{ fontWeight: 700, color: INK }}>
                        {p.kind === "pickup" ? "Pickup" : "Drop"} {p.label}
                      </div>
                      <div style={{ color: MUTED }}>{placeName(p)}</div>
                    </div>
                  </div>
                  {s.provider_ref && <div style={{ color: INK }}>{s.provider_ref}</div>}
                  {p.kind === "drop" && s.client_city && <div style={{ color: MUTED }}>{s.client_city}</div>}
                  {when && <div style={{ color: when.color, fontWeight: 600 }}>{when.text}</div>}
                  {(windowFrom || windowTo) && (
                    <div style={{ color: MUTED }}>
                      Window {windowFrom || "—"} – {windowTo || "—"}
                    </div>
                  )}
                  {s.weight_kg != null && <div style={{ color: MUTED }}>{s.weight_kg} kg</div>}
                  {p.unsequenced && (
                    <div style={{ color: MUTED, marginTop: 2 }}>
                      Not in LogiNext's plan — position in the run unknown
                    </div>
                  )}
                </div>
              );
            })()}
          </InfoWindowF>
        )}

        {liveLocation && (
          <MarkerF
            position={liveLocation}
            zIndex={1999}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: DONE,
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 3,
            }}
            title="Driver's last known position"
          />
        )}
      </GoogleMap>

      {/* Floating controls */}
      <div style={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 8 }}>
        <Tooltip title="Show every stop">
          <Button size="small" onClick={fitAll} style={{ boxShadow: "0 1px 4px rgba(0,0,0,.15)" }}>
            Fit all
          </Button>
        </Tooltip>
      </div>
      <div
        style={{
          position: "absolute",
          left: 12,
          bottom: 12,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          maxWidth: "calc(100% - 24px)",
          padding: "7px 11px",
          background: "rgba(255,255,255,.95)",
          borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,.15)",
          fontSize: 11.5,
          color: INK,
        }}
      >
        {legendItem(<span style={{ width: 11, height: 11, borderRadius: 2, background: PICKUP }} />, "Pickup")}
        {legendItem(<span style={{ width: 11, height: 11, borderRadius: "50%", background: DELIVER }} />, "Drop")}
        {others.length > 0 &&
          legendItem(<span style={{ width: 11, height: 11, borderRadius: 2, background: DEPOT }} />, "Warehouse")}
        {legendItem(<span style={{ width: 16, height: 3, borderRadius: 2, background: ROUTE }} />, "Route")}
        {legendItem(
          <span style={{ width: 11, height: 11, borderRadius: "50%", background: DELIVER, opacity: 0.45 }} />,
          "Done"
        )}
        {nextIdx != null &&
          legendItem(
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#fff", boxShadow: `inset 0 0 0 2.5px ${INK}` }} />,
            "Next"
          )}
        {liveLocation && legendItem(<span style={{ width: 10, height: 10, borderRadius: "50%", background: DONE }} />, "Driver")}
      </div>
    </div>
  );

  return (
    <div
      ref={rootRef}
      style={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        height: height ?? "100%",
        width: "100%",
        background: "#fff",
      }}
    >
      {sidebar}
      {map}
    </div>
  );
};

export default TripMap;
